import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, PlusCircle, FileText, UserCheck, Eye, Filter, X, FolderOpen, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import IssueIdLink from '@/components/IssueIdLink';
import CustomerTutorial from '@/components/CustomerTutorial';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Load filters from localStorage
const loadSavedCustomerFilters = () => {
  try {
    const saved = localStorage.getItem('customer_dashboard_filters');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading saved filters:', e);
  }
  return { project: '', status: '', dateFrom: '', dateTo: '', includeResolved: false };
};

// Check if user has seen the tutorial
const hasSeenTutorial = () => {
  const sessionToken = localStorage.getItem('customer_session');
  if (!sessionToken) return true;
  return localStorage.getItem(`tutorial_completed_${sessionToken}`) === 'true';
};

function CustomerDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [allIssues, setAllIssues] = useState({
    assigned: [],
    reported: [],
    monitored: [],
    project: []
  });
  const [filteredIssues, setFilteredIssues] = useState({
    assigned: [],
    reported: [],
    monitored: [],
    project: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('reported');
  
  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(!hasSeenTutorial());
  
  // Filtri (load from localStorage)
  const [filters, setFilters] = useState(loadSavedCustomerFilters());
  const [showFilters, setShowFilters] = useState(false);
  
  // Opzioni per i filtri
  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem('customer_dashboard_filters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    const userData = localStorage.getItem('customer_user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchIssues();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allIssues]);

  const applyFilters = () => {
    const filterIssues = (issuesList) => {
      return issuesList.filter(issue => {
        // Filtro progetto
        if (filters.project && issue.project?.name !== filters.project) {
          return false;
        }
        
        // Filtro stato
        if (filters.status && issue.status?.name !== filters.status) {
          return false;
        }
        
        // Filtro data from
        if (filters.dateFrom) {
          const issueDate = new Date(issue.created_at);
          const filterDate = new Date(filters.dateFrom);
          if (issueDate < filterDate) {
            return false;
          }
        }
        
        // Filtro data to
        if (filters.dateTo) {
          const issueDate = new Date(issue.created_at);
          const filterDate = new Date(filters.dateTo);
          filterDate.setHours(23, 59, 59, 999); // Fine giornata
          if (issueDate > filterDate) {
            return false;
          }
        }
        
        return true;
      }).sort((a, b) => b.id - a.id); // Sort by ID descending
    };

    setFilteredIssues({
      assigned: filterIssues(allIssues.assigned),
      reported: filterIssues(allIssues.reported),
      monitored: filterIssues(allIssues.monitored),
      project: filterIssues(allIssues.project)
    });
  };

  const handleClearFilters = () => {
    setFilters({
      project: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const fetchIssues = async () => {
    setLoading(true);
    setError('');

    try {
      const sessionToken = localStorage.getItem('customer_session');
      
      // Fetch both "all" (user's issues) and "project" (all project issues)
      const [userResponse, projectResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customer/my-issues`, {
          params: {
            session_token: sessionToken,
            issue_type: 'all'
          }
        }),
        axios.get(`${BACKEND_URL}/api/customer/my-issues`, {
          params: {
            session_token: sessionToken,
            issue_type: 'project'
          }
        })
      ]);

      // Group user's issues by type
      const allIssuesData = userResponse.data.issues;
      const userId = userResponse.data.user_id;

      const grouped = {
        assigned: allIssuesData.filter(i => i.handler?.id === userId),
        reported: allIssuesData.filter(i => i.reporter?.id === userId),
        monitored: allIssuesData.filter(i => i.monitors?.some(m => m.id === userId)),
        project: projectResponse.data.issues || []
      };

      setAllIssues(grouped);
      
      // Estrai opzioni per i filtri (usa tutte le issue del progetto per avere più opzioni)
      const allProjectIssues = projectResponse.data.issues || [];
      const uniqueProjects = [...new Set(allProjectIssues.map(i => i.project?.name).filter(Boolean))];
      const uniqueStatuses = [...new Set(allProjectIssues.map(i => i.status?.name).filter(Boolean))];
      
      setProjects(uniqueProjects.sort());
      setStatuses(uniqueStatuses.sort());
      
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        setError(err.response?.data?.detail || 'Errore nel caricamento delle issue');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_session');
    localStorage.removeItem('customer_user');
    onLogout();
  };

  // Tutorial handlers
  const handleTutorialComplete = async () => {
    const sessionToken = localStorage.getItem('customer_session');
    if (sessionToken) {
      localStorage.setItem(`tutorial_completed_${sessionToken}`, 'true');
      
      // Log tutorial completion
      try {
        await axios.post(`${BACKEND_URL}/api/customer/tutorial-completed`, {
          session_token: sessionToken
        });
      } catch (err) {
        console.error('Error logging tutorial completion:', err);
      }
    }
    setShowTutorial(false);
  };

  const handleShowTutorial = () => {
    setShowTutorial(true);
  };

  const renderIssuesList = (issuesList) => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <div className="text-[rgb(161,161,170)] font-mono">Caricamento...</div>
        </div>
      );
    }

    if (issuesList.length === 0) {
      return (
        <div className="text-center py-12">
          <FileText className="h-16 w-16 text-[rgb(113,113,122)] mx-auto mb-4" />
          <p className="text-[rgb(161,161,170)] font-mono">
            {hasActiveFilters ? 'Nessuna segnalazione trovata con questi filtri' : 'Nessuna segnalazione trovata'}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {issuesList.map((issue) => (
          <div
            key={issue.id}
            onClick={() => navigate(`/customer/issue/${issue.id}`)}
            className="bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded-lg p-4 hover:border-[rgb(82,82,91)] transition-colors cursor-pointer"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-[rgb(59,130,246)] font-mono font-bold">#{issue.id}</span>
                <span className={`px-2 py-1 rounded text-xs font-mono ${
                  issue.status?.name?.toLowerCase().includes('resolved') || issue.status?.name?.toLowerCase().includes('closed')
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {issue.status?.name || 'N/A'}
                </span>
              </div>
              <div className="text-right">
                <div className="text-xs text-[rgb(161,161,170)] font-mono">
                  {new Date(issue.created_at).toLocaleDateString('it-IT')}
                </div>
              </div>
            </div>

            <h3 className="text-[rgb(250,250,250)] font-mono font-semibold mb-2">
              {issue.summary}
            </h3>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-[rgb(113,113,122)] font-mono">Progetto: </span>
                <span className="text-[rgb(161,161,170)] font-mono">{issue.project?.name || 'N/A'}</span>
              </div>
              <div>
                <span className="text-[rgb(113,113,122)] font-mono">Categoria: </span>
                <span className="text-[rgb(161,161,170)] font-mono">{issue.category?.name || 'N/A'}</span>
              </div>
              <div>
                <span className="text-[rgb(113,113,122)] font-mono">Priorità: </span>
                <span className="text-[rgb(161,161,170)] font-mono">{issue.priority?.name || 'N/A'}</span>
              </div>
              {issue.due_date && (
                <div>
                  <span className="text-[rgb(113,113,122)] font-mono">Scadenza: </span>
                  <span className="text-[rgb(161,161,170)] font-mono">
                    {new Date(issue.due_date).toLocaleDateString('it-IT')}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[rgb(9,9,11)]">
      {/* Tutorial */}
      {showTutorial && (
        <CustomerTutorial 
          onComplete={handleTutorialComplete} 
          onSkip={handleTutorialComplete}
        />
      )}
      
      {/* Header */}
      <div className="bg-[rgb(24,24,27)] border-b border-[rgb(39,39,42)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img 
                src="/assets/logo_gmed.svg" 
                alt="Gmed Logo" 
                className="h-8"
              />
              <div>
                <h1 className="text-xl font-bold font-mono text-[rgb(250,250,250)]">
                  Portale Clienti
                </h1>
                {user && (
                  <p className="text-xs text-[rgb(161,161,170)] font-mono">
                    {user.email}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleShowTutorial}
                variant="ghost"
                size="icon"
                className="text-[rgb(161,161,170)] hover:text-white"
                title="Mostra Tutorial"
              >
                <HelpCircle className="h-5 w-5" />
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-[rgb(161,161,170)] hover:text-white font-mono"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500 rounded p-4 mb-6">
            <p className="text-red-500 font-mono">{error}</p>
          </div>
        )}

        {/* Filtri */}
        <div className="mb-6" data-testid="filters-section">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            className="border-[rgb(63,63,70)] text-[rgb(161,161,170)] hover:text-white font-mono mb-4"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Nascondi' : 'Mostra'} Filtri
            {hasActiveFilters && <span className="ml-2 bg-[rgb(59,130,246)] text-white px-2 py-0.5 rounded-full text-xs">Attivi</span>}
          </Button>

          {showFilters && (
            <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Filtro Progetto */}
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                    Progetto
                  </label>
                  <Select value={filters.project} onValueChange={(value) => setFilters({...filters, project: value === 'all' ? '' : value})}>
                    <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                      <SelectValue placeholder="Tutti" />
                    </SelectTrigger>
                    <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                      <SelectItem value="all" className="font-mono">Tutti</SelectItem>
                      {projects.map(project => (
                        <SelectItem key={project} value={project} className="font-mono">
                          {project}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtro Stato */}
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                    Stato
                  </label>
                  <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value === 'all' ? '' : value})}>
                    <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                      <SelectValue placeholder="Tutti" />
                    </SelectTrigger>
                    <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                      <SelectItem value="all" className="font-mono">Tutti</SelectItem>
                      {statuses.map(status => (
                        <SelectItem key={status} value={status} className="font-mono">
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtro Data Da */}
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                    Data Da
                  </label>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>

                {/* Filtro Data A */}
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                    Data A
                  </label>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
              </div>

              {hasActiveFilters && (
                <Button
                  onClick={handleClearFilters}
                  variant="ghost"
                  className="text-[rgb(161,161,170)] hover:text-white font-mono"
                >
                  <X className="h-4 w-4 mr-2" />
                  Rimuovi Filtri
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/customer/create')}
            className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono"
            data-testid="new-issue-button"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Nuova Segnalazione
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] mb-6 flex-wrap h-auto gap-1 p-1" data-testid="customer-tabs">
            <TabsTrigger
              value="reported"
              className="data-[state=active]:bg-[rgb(59,130,246)] data-[state=active]:text-white font-mono"
            >
              <FileText className="h-4 w-4 mr-2" />
              Segnalate da me ({filteredIssues.reported.length})
            </TabsTrigger>
            <TabsTrigger
              value="assigned"
              className="data-[state=active]:bg-[rgb(59,130,246)] data-[state=active]:text-white font-mono"
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Assegnate a me ({filteredIssues.assigned.length})
            </TabsTrigger>
            <TabsTrigger
              value="monitored"
              className="data-[state=active]:bg-[rgb(59,130,246)] data-[state=active]:text-white font-mono"
            >
              <Eye className="h-4 w-4 mr-2" />
              Monitorate ({filteredIssues.monitored.length})
            </TabsTrigger>
            <TabsTrigger
              value="project"
              className="data-[state=active]:bg-[rgb(59,130,246)] data-[state=active]:text-white font-mono"
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              Tutte del Progetto ({filteredIssues.project.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reported">
            {renderIssuesList(filteredIssues.reported)}
          </TabsContent>

          <TabsContent value="assigned">
            {renderIssuesList(filteredIssues.assigned)}
          </TabsContent>

          <TabsContent value="monitored">
            {renderIssuesList(filteredIssues.monitored)}
          </TabsContent>

          <TabsContent value="project">
            {renderIssuesList(filteredIssues.project)}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default CustomerDashboard;
